import { all, takeLatest, put, call, select } from 'redux-saga/effects';
import renderNotification from '../../helpers/notifications/renderNotification';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import { INSURANCE_ORGANIZATIONS, ALLOWED_SESSION_TYPES } from '../../consts/consts';
import { selectAllInsuredRiskIds } from '../insuredRisks/selectors';
import { setCurrentUserOrganizationName } from '../authentication/slice';
import { selectCurrentUser } from '../authentication/selectors';
import { onGetOrganizations, onCreateOrganization, onUpdateOrganization } from './api';
import {
  getOrganizations,
  getOrganizationsSucceeded,
  getOrganizationsFailed,
  setSelectedOrganizationId,
  createOrganization,
  createOrganizationSucceeded,
  createOrganizationFailed,
  updateOrganization,
  updateOrganizationSucceeded,
  updateOrganizationFailed,
  bulkEditOrganizations,
  bulkEditOrganizationsSucceeded,
  bulkEditOrganizationsFailed,
  setInsuranceOrganizationLogos
} from './slice';

const { organization_create_success, generic_change_success } = NOTIFICATION_MESSAGES;

export function* getOrganizationsWorker({ payload }) {
  try {
    const response = yield call(onGetOrganizations, payload);
    yield put(getOrganizationsSucceeded(response.data));
  } catch {
    yield put(getOrganizationsFailed());
  }
}

export function* setInsuranceOrganizationLogosWorker({ payload }) {
  const organizations = payload;

  const insuranceOrganizationLogos = organizations.reduce((logos, org) => {
    if (org.type !== 'insurance' || !org.name) {
      return logos;
    }

    const normalizedOrgName = org.name.trim().toLowerCase();

    const matchingLogoKey = Object.keys(INSURANCE_ORGANIZATIONS).find((logoKey) =>
      INSURANCE_ORGANIZATIONS[logoKey].some((alias) => alias.trim().toLowerCase() === normalizedOrgName)
    );

    if (matchingLogoKey) {
      logos[org.id] = matchingLogoKey;
    }

    return logos;
  }, {});

  yield put(setInsuranceOrganizationLogos(insuranceOrganizationLogos));
}

export function* createOrganizationWorker({ payload }) {
  const { organization, callback } = payload;
  const { type } = organization;
  // This is a temporary solution as long as the is no UI for the user to select the desired authentication type.
  const _organization = {
    ...organization,
    allowed_session_types: [ALLOWED_SESSION_TYPES.PASSWORD],
    // Default notification settings — same logic as bulkEditOrganizationsWorker:
    // Insurance: only new_messages_email_notification enabled.
    // Immo / Reparatur: all four enabled.
    new_messages_email_notification: organization.new_messages_email_notification ?? true,
    new_messages_worklist_notification: organization.new_messages_worklist_notification ?? type !== 'insurance',
    new_states_email_notification: organization.new_states_email_notification ?? type !== 'insurance',
    new_states_worklist_notification: organization.new_states_worklist_notification ?? type !== 'insurance'
  };
  try {
    const response = yield call(onCreateOrganization, _organization);
    renderNotification({ type: 'success', message: organization_create_success });
    yield put(getOrganizations());
    yield put(createOrganizationSucceeded());
    yield put(setSelectedOrganizationId({ organizationId: response.data.id }));
    callback?.();
  } catch {
    yield put(createOrganizationFailed());
  }
}

export function* updateOrganizationWorker({ payload }) {
  // `renderSuccess` (default true) controls the toast — Onboarding disables it for chained updates.
  const { organization, callback, renderSuccess = true } = payload;
  try {
    yield call(onUpdateOrganization, organization);
    if (renderSuccess) {
      renderNotification({ type: 'success', message: generic_change_success });
    }
    yield put(getOrganizations());
    // Wenn der User seine EIGENE Org bearbeitet hat, ist der in currentUser.meta_data
    // gecachte Org-Name (z. B. im Topbar-Avatar-Menü) jetzt veraltet. Wir patchen ihn lokal
    // mit dem neuen Namen, statt validateUser zu dispatchen — letzteres würde die ganze App
    // wegen validateUserStatus === LOADING kurz unmounten (Flackern, siehe router.js).
    const currentUser = yield select(selectCurrentUser);
    if (currentUser?.organization_id && organization?.id === currentUser.organization_id) {
      yield put(setCurrentUserOrganizationName(organization.name));
    }
    yield put(updateOrganizationSucceeded());
    callback?.();
  } catch {
    yield put(updateOrganizationFailed());
  }
}

export function* bulkEditOrganizationsWorker({ payload }) {
  const { orgsToCreate, orgsToUpdate, callback } = payload;
  const allInsuredRiskIds = yield select(selectAllInsuredRiskIds);

  // Per-item try/catch instead of one big try around the whole loop: a single failing org
  // shouldn't prevent the rest from being processed. After the run we dispatch FAILED if any
  // item threw (callers stay informed), SUCCEEDED + callback only if all items succeeded.
  let anyFailed = false;

  // 1. Create (sequential)
  for (const org of orgsToCreate) {
    const { type, name, email } = org;

    // Ensure all required fields are set
    if (!type || !name || !email) {
      continue;
    }

    const orgData = {
      type,
      name: name.trim(),
      email: email.trim(),
      allowed_session_types: [ALLOWED_SESSION_TYPES.PASSWORD],
      // Default notification settings: All enabled except for insurance organizations
      // Insurance: only email notifications for new messages
      // Others: all notifications enabled (worklist + email for states and messages)
      new_states_worklist_notification: type !== 'insurance',
      new_states_email_notification: type !== 'insurance',
      new_messages_worklist_notification: type !== 'insurance',
      new_messages_email_notification: true
    };

    if (type === 'housing_company' || type === 'service_provider') {
      orgData.allowed_insured_risk_ids = allInsuredRiskIds;
    }

    try {
      yield call(onCreateOrganization, orgData);
    } catch {
      anyFailed = true;
    }
  }

  // 2. Update (sequential)
  for (const org of orgsToUpdate) {
    // Ensure all required fields are set
    if (!org.id || !org.name || !org.email) {
      continue;
    }

    try {
      yield call(onUpdateOrganization, org);
    } catch {
      anyFailed = true;
    }
  }

  if (anyFailed) {
    yield put(bulkEditOrganizationsFailed());
  } else {
    yield put(bulkEditOrganizationsSucceeded());
    callback?.();
  }
}

export default function* organizationsSaga() {
  yield all([
    takeLatest(getOrganizations.type, getOrganizationsWorker),
    takeLatest(getOrganizationsSucceeded.type, setInsuranceOrganizationLogosWorker),
    takeLatest(createOrganization.type, createOrganizationWorker),
    takeLatest(updateOrganization.type, updateOrganizationWorker),
    takeLatest(bulkEditOrganizations.type, bulkEditOrganizationsWorker)
  ]);
}
